import type {
    Answers,
    QuizConfig,
} from "@wags/quiz-types/components/quiz/types.ts";
import type { QuizScoreOutput } from "@wags/quiz-types/lib/quizScore.ts";
import { z } from "zod";

const quizAnswerValueContract = z.string().or(z.array(z.string()));

export const quizAnswersContract = z.record(
    z.string(),
    quizAnswerValueContract,
);

export const QUIZ_SCHEMA_VERSION = 1;

export const QUIZ_PARAM_PREFIX = "quiz_";
export const QUIZ_VERSION_PARAM = "quiz_v";

export const KNOWN_QUIZ_KEYS = [
    "trainable",
    "energy",
    "size",
    "shedding",
    "family",
    "grooming",
] as const;

export const quizAnswerParamsSchema = z.looseObject({
    quiz_v: z.string(),
    quiz_trainable: z.string().optional(),
    quiz_energy: z.string().optional(),
    quiz_size: z.string().optional(),
    quiz_shedding: z.string().optional(),
    quiz_family: z.string().optional(),
    quiz_grooming: z.string().optional(),
});
export type QuizAnswerParams = z.infer<typeof quizAnswerParamsSchema>;

const scoreContract = z.looseObject({
    label: z.string(),
    score: z.looseObject({
        numerator: z.number(),
        denominator: z.number(),
    }),
});

const recommendationItemContract = z.looseObject({
    title: z.string(),
    description: z.string(),
    image: z.string(),
    price: z.string(),
});

const recommendationCategoryContract = z.looseObject({
    title: z.string(),
    cards: z.array(recommendationItemContract),
});

const healthReviewConfigContract = z.looseObject({
    title: z.string(),
    subtitle: z.string(),
    image: z.string(),
    scores: z.array(scoreContract),
});

const recommendationsConfigContract = z.looseObject({
    categories: z.array(recommendationCategoryContract).optional().nullable(),
    rec: z.array(recommendationItemContract),
});

const artifactTypeContract = z.union([
    z.literal("animated-sketch"),
    z.literal("polaroid"),
]);

const resultTypeContract = z.literal("recommendations");

const questionBaseContract = z.looseObject({
    id: z.string(),
    question: z.string(),
    description: z.string().optional(),
    required: z.boolean().optional(),
});

const singleSelectQuestionContract = questionBaseContract.extend({
    type: z.literal("single-select"),
    options: z.array(z.string()),
});

const multiSelectQuestionContract = questionBaseContract.extend({
    type: z.literal("multi-select"),
    options: z.array(z.string()),
});

const freeformQuestionContract = questionBaseContract.extend({
    type: z.literal("freeform"),
    placeholder: z.string().optional(),
});

const multipleChoiceQuestionContract = z.discriminatedUnion("type", [
    singleSelectQuestionContract,
    multiSelectQuestionContract,
    freeformQuestionContract,
]);

const voiceQuizStepContract = z.looseObject({
    id: z.string(),
    prompt: z.string(),
    completionPrompt: z.string(),
    media: z.string(),
    promptAudio: z.string(),
    responseAudio: z.string(),
});

const quizConfigBaseContract = z.looseObject({
    id: z.string(),
    showOnboarding: z.boolean(),
    artifactType: artifactTypeContract,
    rubric: z.string().optional(),
    resultType: resultTypeContract,
    healthReviewConfig: healthReviewConfigContract,
    recommendationsConfig: recommendationsConfigContract,
});

const multipleChoiceQuizConfigContract = quizConfigBaseContract.extend({
    quizType: z.literal("multiple-choice"),
    questions: z.array(multipleChoiceQuestionContract),
});

const voiceQuizConfigContract = quizConfigBaseContract.extend({
    quizType: z.literal("voice"),
    steps: z.array(voiceQuizStepContract),
});

export const quizConfigContract = z.discriminatedUnion("quizType", [
    multipleChoiceQuizConfigContract,
    voiceQuizConfigContract,
]);

export const quizResultsContract = z.looseObject({
    resultType: resultTypeContract,
    healthReview: healthReviewConfigContract,
    recommendations: recommendationsConfigContract,
});

export type QuizAnswersContract = z.output<typeof quizAnswersContract>;
export type QuizConfigContract = z.output<typeof quizConfigContract>;
export type QuizResultsContract = z.output<typeof quizResultsContract>;

type AssertAssignable<From, To> = [From] extends [To] ? true : never;
type AssertTrue<T extends true> = T;

export type _quizAnswersContractMatches = AssertTrue<
    AssertAssignable<QuizAnswersContract, Answers>
>;
export type _quizConfigContractMatches = AssertTrue<
    AssertAssignable<QuizConfigContract, QuizConfig>
>;
export type _quizResultsContractMatches = AssertTrue<
    AssertAssignable<QuizResultsContract, QuizScoreOutput>
>;
